import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Lesson, TelegramUser } from "../types";
import { useCourseProgress } from "../hooks/useCourseProgress";
import { useCourse } from "../hooks/useCourses";
import { useLesson } from "../hooks/useLesson";
import { SEOHead } from "../components/SEOHead";
import { generateArticleSchema, generateBreadcrumbSchema, SITE_URL } from "../utils/seo";
import { ChevronLeft, ChevronRight, BookOpen, Home, PanelLeftClose, PanelLeftOpen, Link as LinkIcon, MessageCircle, X, CheckCircle, PlayCircle, Map } from "lucide-react";
import { CourseCodeBlock } from "../components/CourseCodeBlock";
import { Quiz } from "../components/Quiz";
import { InlineSQLCheck } from "../components/InlineSQLCheck";
import { LessonLinks } from "../components/LessonLinks";
import { ModuleCompletion } from "../components/ModuleCompletion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock } from "lucide-react";
import { API_HOST } from "../constants";
import { AITutorDualModePanel, type AITutorDualMode } from "../components/AITutorDualModePanel";
import { useResize } from "../hooks/useResize";
import { useMobile } from "../hooks/useMobile";
import { OnboardingTour } from "../components/OnboardingTour";
import { useOnboardingCompleted } from "../hooks/useOnboardingCompleted";
import {
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
  AI_TUTOR_DESKTOP_TOP,
} from "../constants/aiTutorLayout";
import { LAYERS } from "../constants/layers";

interface LessonPageProps {
  user: TelegramUser | null;
}

export const LessonPage = ({ user }: LessonPageProps) => {
  const { slug, lessonId } = useParams();
  const navigate = useNavigate();

  const { course, loading: courseLoading, error: courseError } = useCourse(slug);
  const { lesson: lessonContent, loading: lessonLoading, error: lessonError } = useLesson(lessonId);
  const { progress, setLessonStatus, stats } = useCourseProgress(user, slug || "");
  const isMobile = useMobile();
  const [sidebarVisible, setSidebarVisible] = useState(true); // По умолчанию открыта, как в roadmap.sh
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());
  const [hoveredLessonId, setHoveredLessonId] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [showModuleCompletion, setShowModuleCompletion] = useState(false);
  const [showCompleteLessonDialog, setShowCompleteLessonDialog] = useState(false);
  const [pendingNextLesson, setPendingNextLesson] = useState<{ id: string; title: string } | null>(null);
  const [showRightPanel, setShowRightPanel] = useState(true); // Открыт по умолчанию на desktop
  const [tutorAsideMode, setTutorAsideMode] = useState<AITutorDualMode>("chat");
  const [externalTutorPrompt, setExternalTutorPrompt] = useState<{ id: string; text: string } | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const onboardingStartRef = useRef<(() => void) | null>(null);
  const {
    completedPages: onboardingCompletedPages,
    isLoading: onboardingStatusLoading,
    refetch: refetchOnboardingStatus,
  } = useOnboardingCompleted(user);

  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(436);
  const rightPanelInitialWidth = useState(() => {
    try {
      const w = localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY);
      if (w) {
        const n = parseInt(w, 10);
        if (n >= AI_TUTOR_MIN_WIDTH && n <= AI_TUTOR_MAX_WIDTH) return n;
      }
    } catch {}
    return AI_TUTOR_DEFAULT_WIDTH;
  })[0];
  const rightPanelResize = useResize({
    initialSize: rightPanelInitialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true,
    onSizeChange: (s) => {
      try {
        localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(s));
      } catch {}
    },
  });

  const handleAskTutorFromLesson = useCallback((prompt: string) => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    setShowRightPanel(true);
    setTutorAsideMode("chat");
    setExternalTutorPrompt({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      text: trimmed,
    });
  }, []);

  // Открываем сайдбар по умолчанию на больших экранах (как в roadmap.sh)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setSidebarVisible(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Правая панель AI Tutor открыта по умолчанию на desktop
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const stored = localStorage.getItem("ai-tutor-lesson-panel-open");
    if (stored !== null) {
      setShowRightPanel(mq.matches && stored === "true");
    } else {
      setShowRightPanel(mq.matches);
    }
    const handleChange = () => {
      if (!mq.matches) setShowRightPanel(false);
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);
  useEffect(() => {
    localStorage.setItem("ai-tutor-lesson-panel-open", String(showRightPanel));
  }, [showRightPanel]);

  // Находим метаданные урока (из курса) и его модуль
  const { lessonMeta, currentModule } = useMemo(() => {
    if (!course || !lessonId) return { lessonMeta: undefined, currentModule: undefined };
    for (const module of course.modules) {
      const found = module.lessons.find((l) => l.id === lessonId);
      if (found) return { lessonMeta: found, currentModule: module };
    }
    return { lessonMeta: undefined, currentModule: undefined };
  }, [course, lessonId]);

  // Объединяем метаданные из курса с контентом из API
  const lesson: Lesson | undefined = useMemo(() => {
    if (!lessonMeta) return undefined;
    if (!lessonContent) return lessonMeta; // Пока контент не загружен, возвращаем метаданные
    
    return {
      ...lessonMeta,
      content: lessonContent.content,
      quiz: lessonContent.quiz,
      links: lessonContent.links,
      estimatedTime: lessonContent.estimatedTime || lessonMeta.estimatedTime,
    };
  }, [lessonMeta, lessonContent]);

  // Все уроки для навигации Next/Prev (используем метаданные из курса)
  const flatLessons = useMemo(() => {
    if (!course) return [];
    return course.modules.flatMap((m) => m.lessons);
  }, [course]);

  const currentIndex = flatLessons.findIndex((l) => l.id === lessonId);
  const prevLesson = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 && currentIndex < flatLessons.length - 1 ? flatLessons[currentIndex + 1] : null;

  // Определяем, является ли урок последним в модуле
  const isLastLessonInModule = useMemo(() => {
    if (!currentModule || !lessonId) return false;
    const moduleLessons = currentModule.lessons;
    const lastLesson = moduleLessons[moduleLessons.length - 1];
    return lastLesson?.id === lessonId;
  }, [currentModule, lessonId]);

  // Автоматически отмечаем как "в работе" при открытии
  useEffect(() => {
    if (lessonMeta && lessonId) {
      const status = progress[lessonId] || "not_started";
      if (status === "not_started") {
        setLessonStatus(lessonId, "in_progress");
      }
    }
  }, [lessonMeta, lessonId, progress, setLessonStatus]);

  // Функция для прокрутки к якорной ссылке
  const scrollToHash = useCallback(() => {
    const hash = window.location.hash;
    if (hash) {
      // Пробуем несколько раз с задержкой, так как контент может загружаться
      let attempts = 0;
      const maxAttempts = 10;
      const tryScroll = () => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(tryScroll, 100);
        }
        return false;
      };
      setTimeout(tryScroll, 100);
    }
  }, []);

  // Прокручиваем страницу в начало при переходе к новому уроку или к якорной ссылке
  useEffect(() => {
    if (lessonId) {
      const hash = window.location.hash;
      if (hash) {
        scrollToHash();
      } else {
        // На мобильных устройствах прокручиваем к началу страницы
        if (isMobile) {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }
  }, [lessonId, scrollToHash, isMobile]);

  // Обрабатываем hash после загрузки контента урока
  useEffect(() => {
    if (lesson && lesson.content && window.location.hash) {
      scrollToHash();
    }
  }, [lesson, scrollToHash]);

  // Обрабатываем изменение hash в браузере (когда пользователь кликает на якорную ссылку)
  useEffect(() => {
    const handleHashChange = () => {
      scrollToHash();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [scrollToHash]);

  // Собираем все task_id из контента урока
  const lessonTaskIds = useMemo(() => {
    if (!lesson?.content) return [];
    const taskIds: number[] = [];
    const regex = /\[code:(\d+)/g;
    let match;
    while ((match = regex.exec(lesson.content)) !== null) {
      const taskId = parseInt(match[1], 10);
      if (!isNaN(taskId)) {
        taskIds.push(taskId);
      }
    }
    return taskIds;
  }, [lesson?.content]);

  // Проверяем прогресс задач из БД
  useEffect(() => {
    if (!user || lessonTaskIds.length === 0) return;

    const checkTaskProgress = async () => {
      try {
        const response = await fetch(`${API_HOST}/user/${user.id}`);
        if (!response.ok) return;
        
        const userData = await response.json();
        const taskProgress = userData.progress || {};
        
        const completed: number[] = [];
        lessonTaskIds.forEach(taskId => {
          if (taskProgress[taskId]?.status === "success") {
            completed.push(taskId);
          }
        });
        
        if (completed.length > 0) {
          setCompletedTasks(new Set(completed));
        }
      } catch (error) {
        console.error("Error checking task progress:", error);
      }
    };

    checkTaskProgress();
  }, [user, lessonTaskIds]);

  // Автоматически завершаем урок, если все задачи выполнены
  useEffect(() => {
    if (!lessonId || !user) return;
    
    // Если есть задачи и все они выполнены
    if (lessonTaskIds.length > 0 && completedTasks.size === lessonTaskIds.length) {
      const currentStatus = progress[lessonId] || "not_started";
      if (currentStatus !== "done") {
        setLessonStatus(lessonId, "done");
      }
    }
    // Если задач нет - при переходе на следующий урок пользователю будет предложено пометить текущий урок как завершенный (логика в handleNextLesson)
  }, [lessonId, lessonTaskIds, completedTasks, progress, setLessonStatus, user]);

  // Отслеживаем предыдущий статус урока для определения, был ли он только что завершен
  const prevLessonStatusRef = useRef<string | null>(null);
  
  // Сбрасываем предыдущий статус при изменении урока
  useEffect(() => {
    if (lessonId) {
      const currentStatus = progress[lessonId] || "not_started";
      prevLessonStatusRef.current = currentStatus;
    }
  }, [lessonId]);
  
  // Показываем модалку завершения модуля только один раз, когда урок только что был завершен
  useEffect(() => {
    if (!lessonId || !isLastLessonInModule || !currentModule || showModuleCompletion) {
      return;
    }
    
    const currentStatus = progress[lessonId] || "not_started";
    const prevStatus = prevLessonStatusRef.current;
    
    // Показываем модалку только если статус только что изменился на "done"
    // (т.е. урок был только что завершен, а не был завершен ранее)
    if (currentStatus === "done" && prevStatus !== "done") {
      // Проверяем, не показывали ли мы уже модалку для этого модуля
      const moduleCompletionKey = `module-completion-${currentModule.id}`;
      const wasShown = localStorage.getItem(moduleCompletionKey);
      
      if (!wasShown) {
        // Небольшая задержка для плавного перехода
        const timer = setTimeout(() => {
          setShowModuleCompletion(true);
          // Помечаем, что модалка была показана для этого модуля
          localStorage.setItem(moduleCompletionKey, "true");
        }, 500);
        return () => clearTimeout(timer);
      }
    }
    
    // Сохраняем текущий статус для следующей проверки
    prevLessonStatusRef.current = currentStatus;
  }, [lessonId, progress, isLastLessonInModule, currentModule, showModuleCompletion]);

  const onboardingSteps = useMemo(() => {
    const steps = [
      {
        id: "sidebar",
        target: "[data-onboarding-id='sidebar']",
        title: "Навигация по курсу",
        content:
          "В левой панели отображается структура курса с модулями и уроками. Вы можете переключаться между уроками, видеть свой прогресс и статус каждого урока. Цветные индикаторы показывают: зеленый — завершено, оранжевый — в процессе, серый — не начато.",
        position: isMobile ? ("bottom" as const) : ("right" as const),
      },
      {
        id: "lesson-content",
        target: "[data-onboarding-id='lesson-content']",
        title: "Контент урока",
        content:
          "Здесь отображается основной материал урока. Вы можете читать теорию, выполнять практические задания и изучать примеры кода. Контент поддерживает markdown-разметку с кодом, таблицами и другими элементами.",
        position: "bottom" as const,
      },
      {
        id: "quiz",
        target: "[data-onboarding-id='quiz']",
        title: "Квиз",
        content:
          "В уроках могут быть квизы для проверки знаний. Отвечайте на вопросы, чтобы закрепить материал. Квизы могут содержать вопросы с одним или несколькими правильными ответами.",
        position: "top" as const,
      },
      {
        id: "ai-tutor",
        target: "[data-onboarding-id='ai-tutor']",
        title: "AI Tutor",
        content:
          "Кнопка с иконкой сообщения открывает AI Tutor — вашего помощника по уроку. Вы можете задавать вопросы о материале, получать объяснения и помощь в решении задач. AI Tutor понимает контекст текущего урока.",
        position: "left" as const,
      },
      {
        id: "lesson-navigation",
        target: "[data-onboarding-id='lesson-navigation']",
        title: "Навигация между уроками",
        content:
          "Внизу страницы находятся кнопки для перехода к предыдущему и следующему уроку. Это позволяет последовательно проходить курс и легко перемещаться между уроками.",
        position: "top" as const,
      },
      {
        id: "complete-lesson",
        target: "[data-onboarding-id='complete-lesson']",
        title: "Завершение урока",
        content:
          "Кнопка 'Отметить как завершенное' позволяет отметить урок как пройденный. Это помогает отслеживать прогресс по курсу. После завершения всех уроков модуля вы увидите сообщение о завершении модуля.",
        position: "bottom" as const,
      },
    ];
    return steps;
  }, [isMobile]);

  if (courseLoading) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (courseError || !course) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-heading font-bold text-neutral-text mb-2">Курс не найден</h1>
          <p className="text-neutral-text/70 mb-4">
            {courseError || "Проверьте адрес или вернитесь к списку курсов."}
          </p>
          <Link
            to="/courses"
            className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Назад к курсам
          </Link>
        </div>
      </div>
    );
  }

  if (!lessonMeta) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-heading font-bold text-neutral-text mb-2">Урок не найден</h1>
          <p className="text-neutral-text/70 mb-4">Проверьте адрес или вернитесь к курсу.</p>
          <Link
            to={`/courses/${slug}`}
            className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Назад к курсу
          </Link>
        </div>
      </div>
    );
  }

  // Показываем загрузку контента урока
  if (lessonLoading && !lesson?.content) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-neutral-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-full mb-2"></div>
            <div className="h-4 bg-neutral-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (lessonError && !lesson?.content) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-heading font-bold text-neutral-text mb-2">Ошибка загрузки урока</h1>
          <p className="text-neutral-text/70 mb-4">{lessonError}</p>
          <Link
            to={`/courses/${slug}`}
            className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Назад к курсу
          </Link>
        </div>
      </div>
    );
  }

  // Если нет контента, но есть метаданные - показываем ошибку
  if (!lesson || !lesson.content) {
    return (
      <div className="pt-4 sm:pt-6 lg:pt-8 max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-white border border-neutral-border rounded-xl p-6 shadow-sm">
          <h1 className="text-2xl font-heading font-bold text-neutral-text mb-2">Контент урока недоступен</h1>
          <p className="text-neutral-text/70 mb-4">Не удалось загрузить контент урока.</p>
          <Link
            to={`/courses/${slug}`}
            className="inline-block px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            Назад к курсу
          </Link>
        </div>
      </div>
    );
  }

  const lessonStatus = progress[lessonId || ""] || "not_started";

  const handlePrevLesson = () => {
    if (prevLesson) {
      // При переходе на предыдущий урок текущий остается в текущем статусе
      navigate(`/courses/${slug}/${prevLesson.id}`);
      // Прокручиваем к началу страницы на мобильных устройствах
      if (isMobile) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    }
  };

  const handleNextLesson = () => {
    if (nextLesson && lessonId) {
      const currentStatus = progress[lessonId] || "not_started";
      
      // Если урок уже завершен, переходим сразу
      if (currentStatus === "done") {
        if (!isLastLessonInModule) {
          navigate(`/courses/${slug}/${nextLesson.id}`);
          // Прокручиваем к началу страницы на мобильных устройствах
          if (isMobile) {
            setTimeout(() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
          }
        }
        return;
      }
      
      // Если урок не завершен, показываем диалог подтверждения
      setPendingNextLesson({ id: nextLesson.id, title: nextLesson.title });
      setShowCompleteLessonDialog(true);
    }
  };

  const handleCompleteLessonConfirm = () => {
    if (lessonId && pendingNextLesson) {
      // Помечаем урок как завершенный
      setLessonStatus(lessonId, "done");
      setShowCompleteLessonDialog(false);
      
      // Модалка завершения модуля покажется автоматически через useEffect, если это последний урок модуля
      // Если это не последний урок - переходим сразу
      if (!isLastLessonInModule) {
        navigate(`/courses/${slug}/${pendingNextLesson.id}`);
        // Прокручиваем к началу страницы на мобильных устройствах
        if (isMobile) {
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        }
      }
      setPendingNextLesson(null);
    }
  };

  const handleCompleteLessonCancel = () => {
    if (pendingNextLesson) {
      // Просто переходим без пометки урока как завершенного
      setShowCompleteLessonDialog(false);
      navigate(`/courses/${slug}/${pendingNextLesson.id}`);
      // Прокручиваем к началу страницы на мобильных устройствах
      if (isMobile) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
      setPendingNextLesson(null);
    }
  };

  const handleModuleCompletionClose = () => {
    setShowModuleCompletion(false);
    // После закрытия модалки переходим на следующий урок, если он есть
    if (nextLesson) {
      navigate(`/courses/${slug}/${nextLesson.id}`);
      // Прокручиваем к началу страницы на мобильных устройствах
      if (isMobile) {
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    } else {
      // Если следующего урока нет, возвращаемся к курсу
      navigate(`/courses/${slug}`);
    }
  };

  const renderStatusDot = (lessonId: string) => {
    const status = progress[lessonId] || "not_started";
    const color =
      status === "done" ? "bg-green-500" : status === "in_progress" ? "bg-orange-400" : "bg-neutral-border";
    return <span className={`w-2.5 h-2.5 rounded-full ${color}`} />;
  };

  // SEO данные для урока
  const lessonTitle = lessonMeta?.title || lesson?.title || "Урок";
  const lessonDescription = `Изучайте ${lessonTitle} в курсе ${course?.title || "ClickHouse"}. Практические задания и интерактивные примеры.`;
  const lessonUrl = `${SITE_URL}/courses/${slug}/${lessonId}`;

  return (
    <>
      <SEOHead
        title={`${lessonTitle} | ${course?.title || "Курс"} | Quantile Camp`}
        description={lessonDescription}
        keywords={`${lessonTitle}, ${course?.title}, ClickHouse урок, SQL обучение, интерактивный урок, ClickHouse курс, базы данных урок, аналитика данных, Data Engineering, Big Data обучение, ClickHouse tutorial, SQL практика, онлайн урок, структура ClickHouse, SQL запросы обучение`}
        type="article"
        author="Валентин Борисов"
        structuredData={[
          generateArticleSchema({
            title: lessonTitle,
            description: lessonDescription,
            url: lessonUrl,
            author: "Валентин Борисов"
          }),
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "Курсы", url: "/courses" },
            { name: course?.title || "Курс", url: `/courses/${slug}` },
            { name: lessonTitle, url: `/courses/${slug}/${lessonId}` }
          ])
        ]}
      />
      {showModuleCompletion && currentModule && (
        <ModuleCompletion
          courseSlug={slug || ""}
          moduleId={currentModule.id}
          moduleTitle={currentModule.title}
          user={user}
          onClose={handleModuleCompletionClose}
        />
      )}
      
      {showCompleteLessonDialog && lessonMeta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-neutral-text mb-4">
              Пометить урок как завершенный?
            </h2>
            <p className="text-neutral-text/70 mb-6">
              Вы переходите к следующему уроку. Хотите завершить текущий урок "{lessonMeta.title}" или просто перейти к следующему?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCompleteLessonConfirm}
                className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
              >
                Да, завершить
              </button>
              <button
                onClick={handleCompleteLessonCancel}
                className="flex-1 px-6 py-3 border border-neutral-border rounded-lg hover:bg-neutral-light transition"
              >
                Нет, просто перейти
              </button>
            </div>
          </div>
        </div>
      )}

      <OnboardingTour 
        steps={onboardingSteps} 
        storageKey="onboarding-lessonpage-completed"
        user={user}
        onStartRef={onboardingStartRef}
        onComplete={refetchOnboardingStatus}
      />

      <div
        className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden bg-neutral-light"
        style={{ paddingRight: !isMobile && showRightPanel ? Math.max(rightPanelResize.size, chatPanelMinWidth) : 0 }}
      >
      {/* Mobile toolbar */}
      <div className="lg:hidden flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white border-b border-neutral-border">
        <button
          onClick={() => {
            setSidebarVisible((prev) => {
              const next = !prev;
              if (next) setShowRightPanel(false);
              return next;
            });
          }}
          className={`min-w-[44px] min-h-[44px] px-2.5 py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center ${
            sidebarVisible
              ? "bg-primary/10 text-primary border-2 border-primary/30"
              : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
          }`}
          aria-label={sidebarVisible ? "Скрыть" : "Модули"}
          title={sidebarVisible ? "Скрыть" : "Модули"}
        >
          {sidebarVisible ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
        </button>
        <span className="flex-1 truncate text-sm font-medium text-neutral-text">{lessonMeta?.title || "Урок"}</span>
        <button
          data-onboarding-id="ai-tutor"
          onClick={() => {
            setSidebarVisible(false);
            setTutorAsideMode("chat");
            setShowRightPanel(true);
          }}
          className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
            showRightPanel && tutorAsideMode === "chat"
              ? "bg-primary/15 text-primary border-2 border-primary/40"
              : "bg-white border-2 border-primary/30 shadow-sm text-primary hover:bg-primary/10 hover:border-primary/40"
          }`}
          aria-label="AI Tutor"
        >
          <MessageCircle className="w-5 h-5" />
          <span>Чат</span>
        </button>
      </div>

      {/* Кнопка развернуть модули (когда свёрнуты) */}
      {!sidebarVisible && (
        <button
          onClick={() => setSidebarVisible(true)}
          className="hidden lg:flex fixed left-0 top-[calc(var(--header-height)+0.5rem)] z-30 w-8 h-12 items-center justify-center bg-white border border-neutral-border border-l-0 rounded-r-lg shadow-sm hover:bg-neutral-light transition-colors"
          aria-label="Показать модули"
        >
          <PanelLeftOpen className="w-4 h-4 text-neutral-text/60" />
        </button>
      )}

      {/* Левая боковая панель: модули и уроки (Cursor-like) */}
      <div
        data-onboarding-id="sidebar"
        className={`hidden lg:flex flex-shrink-0 flex-col border-r border-neutral-border bg-white transition-all duration-300 overflow-hidden ${
          sidebarVisible ? "w-[280px]" : "w-0"
        }`}
      >
          {/* Заголовок сайдбара */}
          <div className="p-4 border-b border-neutral-border flex-shrink-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <Link
                  to={`/courses/${slug}`}
                  className="text-sm text-primary hover:text-primary-dark transition mb-2 inline-flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>К курсу</span>
                </Link>
                <h2 className="text-lg font-bold text-neutral-text mb-1">{course?.title || ""}</h2>
              </div>
              <button
                onClick={() => setSidebarVisible(false)}
                className="p-2 rounded-lg hover:bg-neutral-light transition-colors flex-shrink-0"
                aria-label="Скрыть модули"
              >
                <PanelLeftClose className="w-5 h-5 text-neutral-text/60" />
              </button>
            </div>
            <div className="text-sm text-neutral-text/60">
              {course?.modules.length || 0} модулей · {flatLessons.length} уроков
            </div>
            {/* Прогресс */}
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-neutral-text/60 mb-1">
                <span>Прогресс</span>
                <span>{Math.round((stats.done / Math.max(stats.total, 1)) * 100)}%</span>
              </div>
              <div className="w-full bg-neutral-light rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${Math.round((stats.done / Math.max(stats.total, 1)) * 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* Список модулей и уроков */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            {course?.modules.map((module) => (
              <div key={module.id} className="mb-6">
                <div className="text-sm font-semibold text-neutral-text mb-2">{module.title}</div>
                <div className="space-y-1">
                  {module.lessons.map((l) => {
                    const isActive = lesson?.id === l.id;
                    const lessonIndex = flatLessons.findIndex(lesson => lesson.id === l.id) + 1;
                    const status = progress[l.id] || "not_started";
                    return (
                      <button
                        key={l.id}
                        onClick={() => {
                          navigate(`/courses/${slug}/${l.id}`);
                          if (status === "not_started") setLessonStatus(l.id, "in_progress");
                        }}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredLessonId(l.id);
                          setTooltipPosition({
                            top: rect.top + rect.height / 2,
                            left: rect.right + 12
                          });
                        }}
                        onMouseLeave={() => {
                          setHoveredLessonId(null);
                          setTooltipPosition(null);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg transition flex items-center gap-3 ${
                          isActive
                            ? "bg-primary/10 text-primary border border-primary"
                            : "hover:bg-neutral-light border border-transparent"
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${
                          isActive 
                            ? "bg-primary text-white" 
                            : status === "done" 
                            ? "bg-green-500 text-white" 
                            : status === "in_progress"
                            ? "bg-orange-400 text-white"
                            : "bg-neutral-border text-neutral-text/60"
                        }`}>
                          {lessonIndex}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{l.title}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          {/* Тултип с информацией об уроке при наведении (только на десктопе) */}
          {hoveredLessonId && tooltipPosition && (() => {
            const hoveredLesson = course?.modules
              .flatMap(m => m.lessons)
              .find(l => l.id === hoveredLessonId);
            
            if (!hoveredLesson) return null;
            
            const lessonStatus = progress[hoveredLessonId] || "not_started";
            const isCompleted = lessonStatus === "done";
            const isInProgress = lessonStatus === "in_progress";
            
            return (
              <div
                className="hidden lg:block fixed z-[100] pointer-events-none"
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: 'translateY(-50%)'
                }}
              >
                <div className="w-80 rounded-xl border-2 border-neutral-border bg-white shadow-2xl overflow-hidden">
                  {/* Заголовок с цветовой индикацией */}
                  <div className={`p-3 ${
                    isCompleted ? 'bg-green-500/10' : isInProgress ? 'bg-orange-400/10' : 'bg-neutral-100'
                  }`}>
                    <h4 className="font-semibold text-sm text-neutral-text leading-tight">
                      {hoveredLesson.title}
                    </h4>
                  </div>
                  
                  {/* Контент */}
                  <div className="p-3 space-y-2.5">
                    {hoveredLesson.summary && (
                      <p className="text-xs text-neutral-text/70 line-clamp-3 leading-relaxed">
                        {hoveredLesson.summary}
                      </p>
                    )}
                    
                    {/* Статус */}
                    <div className="flex items-center gap-2 pt-2 border-t border-neutral-border/50">
                      <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                        isCompleted ? 'bg-green-500/10' : isInProgress ? 'bg-orange-400/10' : 'bg-neutral-100'
                      }`}>
                        <CheckCircle className={`w-3.5 h-3.5 ${
                          isCompleted ? 'text-green-500' : isInProgress ? 'text-orange-400' : 'text-neutral-text/50'
                        }`} />
                      </div>
                      <div>
                        <div className="text-[10px] text-neutral-text/50 leading-none mb-0.5">Статус</div>
                        <div className="text-xs font-semibold text-neutral-text">
                          {isCompleted ? 'Завершено' : isInProgress ? 'В процессе' : 'Не начато'}
                        </div>
                      </div>
                    </div>
                    
                    {hoveredLesson.estimatedTime && (
                      <div className="flex items-center gap-1.5 text-xs text-neutral-text/60 pt-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>Примерно {hoveredLesson.estimatedTime} мин</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

      {/* Центр: контент урока (скролл). data-lesson-scroll — для InlineSQLCheck: колёсико над редактором прокручивает страницу. */}
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden bg-neutral-light" data-lesson-scroll>
          <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 w-full">
            {/* Заголовок урока: «Урок N из M» и «Завершить» в одну строку; ниже — только h1 */}
            <div className="mb-6">
              <div className="mb-2 sm:hidden">
                <Link
                  to={`/courses/${slug}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary-dark transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>К обзору курса</span>
                </Link>
              </div>
              <div className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-2">
                <span className="text-sm text-neutral-text/60">
                  Урок {currentIndex + 1} из {flatLessons.length}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-onboarding-id="complete-lesson"
                    onClick={() => {
                      if (lessonId) {
                        const currentStatus = progress[lessonId] || "not_started";
                        if (currentStatus !== "done") {
                          setLessonStatus(lessonId, "done");
                        } else {
                          setLessonStatus(lessonId, "in_progress");
                        }
                      }
                    }}
                    className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base font-medium transition flex items-center gap-1.5 sm:gap-2 ${
                      progress[lessonId || ""] === "done"
                        ? "bg-green-500 text-white hover:bg-green-600"
                        : "bg-primary text-white hover:bg-primary-dark"
                    }`}
                  >
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline">
                      {progress[lessonId || ""] === "done" ? "Завершено" : "Отметить как завершенное"}
                    </span>
                    <span className="sm:hidden">{progress[lessonId || ""] === "done" ? "Завершено" : "Завершить"}</span>
                  </button>
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-neutral-text">
                {lessonMeta?.title || lesson?.title || "Урок"}
              </h1>
            </div>

            {/* План модуля (только на мобильных устройствах) */}
            {currentModule && (
              <div className="mb-6 sm:hidden">
                <button
                  onClick={() => setSelectedModuleId(currentModule.id)}
                  className="w-full bg-white border border-neutral-border rounded-lg shadow-sm p-4 text-left hover:border-primary/50 transition"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-neutral-text/60 mb-1">Текущий модуль</div>
                      <div className="text-sm font-semibold text-neutral-text">{currentModule.title}</div>
                      <div className="text-xs text-neutral-text/60 mt-1">
                        {currentModule.lessons.length} уроков
                      </div>
                    </div>
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                </button>
              </div>
            )}

            {/* Контент урока */}
            <div 
              data-onboarding-id="lesson-content"
              className="bg-white rounded-lg shadow-sm pt-1 sm:pt-2 pb-4 sm:pb-6 px-4 sm:px-6 mb-6 w-full"
            >
              {lesson?.content ? (
                <LessonContent 
                  content={lesson.content} 
                  user={user} 
                  quiz={lesson.quiz}
                  onTaskCompleted={(taskId) => {
                    setCompletedTasks(prev => new Set([...prev, taskId]));
                  }}
                  courseSlug={slug}
                  lessonId={lessonId}
                  onAskTutor={handleAskTutorFromLesson}
                />
              ) : (
                <div className="text-neutral-text/60">Загрузка контента...</div>
              )}
            </div>

            {/* Полезные ссылки */}
            {lesson?.links && lesson.links.length > 0 && (
              <div className="mb-6">
                <LessonLinks links={lesson.links} />
              </div>
            )}

            {/* Навигация между уроками */}
            <div 
              data-onboarding-id="lesson-navigation"
              className="flex items-center justify-between pt-6 border-t border-neutral-border"
            >
              <button
                className="px-4 py-2 text-sm rounded-lg border border-neutral-border hover:border-primary transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={!prevLesson}
                onClick={handlePrevLesson}
              >
                <ChevronLeft className="w-4 h-4" />
                <div className="text-left">
                  <div className="text-xs text-neutral-text/60">Предыдущий</div>
                  <div className="font-medium">{prevLesson?.title || "—"}</div>
                </div>
              </button>

              <button
                className="px-4 py-2 text-sm rounded-lg border border-neutral-border hover:border-primary transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={!nextLesson}
                onClick={handleNextLesson}
              >
                <div className="text-right">
                  <div className="text-xs text-neutral-text/60">Следующий</div>
                  <div className="font-medium">{nextLesson?.title || "—"}</div>
                </div>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
      </main>

      {/* Кнопки развернуть AI Tutor / Roadmap (когда панель свёрнута) */}
      {!showRightPanel && (
        <div className="hidden lg:flex fixed right-0 top-[calc(var(--header-height)+0.5rem)] z-30 flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("chat");
              setShowRightPanel(true);
            }}
            className="flex h-10 w-10 items-center justify-center bg-white border border-neutral-border border-r-0 rounded-l-lg shadow-sm hover:bg-primary/5 hover:border-primary/40 transition-colors"
            aria-label="Показать чат AI Tutor"
            title="Чат"
          >
            <MessageCircle className="w-4 h-4 text-primary" />
          </button>
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("roadmap");
              setShowRightPanel(true);
            }}
            className="flex h-10 w-10 items-center justify-center bg-white border border-neutral-border border-r-0 rounded-l-lg shadow-sm hover:bg-primary/5 hover:border-primary/40 transition-colors"
            aria-label="Показать Roadmap"
            title="Roadmap"
          >
            <Map className="w-4 h-4 text-primary" />
          </button>
        </div>
      )}

      {/* Ресайзер правой панели (fixed, у левого края панели) */}
      {showRightPanel && (
        <div
          onMouseDown={rightPanelResize.onMouseDown}
          className="hidden lg:flex fixed z-[56] justify-center cursor-col-resize"
          style={{
            width: "8px",
            top: 0,
            bottom: 0,
            right: Math.max(rightPanelResize.size, chatPanelMinWidth) - 4,
          }}
        >
          <div className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors" style={{ height: "100%" }} />
        </div>
      )}

      {/* Правая панель: AI Tutor — fixed, full height, перекрывает хедер */}
      {showRightPanel && (
        <aside
          ref={rightPanelResize.elementRef}
          className="hidden lg:flex flex-col border-l border-neutral-border bg-white overflow-hidden min-h-0"
          style={{
            position: "fixed",
            top: AI_TUTOR_DESKTOP_TOP,
            right: 0,
            bottom: 0,
            width: Math.max(rightPanelResize.size, chatPanelMinWidth),
            zIndex: LAYERS.aiTutorPanel,
          }}
        >
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <AITutorSidebar
              user={user}
              courseTitle={course?.title || ""}
              lessonTitle={lessonMeta?.title || ""}
              lessonId={lessonId || ""}
              courseSlug={slug || ""}
              lessonContent={lesson?.content || ""}
              onClose={() => setShowRightPanel(false)}
              width={340}
              rightOffset={0}
              isEmbedded={true}
              onMinWidthChange={setChatPanelMinWidth}
              onboardingPageKey="lessonpage"
              onboardingCompletedPages={onboardingCompletedPages}
              onboardingStatusLoading={onboardingStatusLoading}
              onStartOnboarding={() => onboardingStartRef.current?.()}
              externalPrompt={externalTutorPrompt}
              tutorAsideMode={tutorAsideMode}
              onTutorAsideModeChange={setTutorAsideMode}
              roadmapIsMobile={false}
            />
          </div>
        </aside>
      )}
      </div>

      {/* Mobile: правая панель overlay — без вкладок, переключение только через кнопки в шапке страницы. Шапка чата с тремя кнопками — внутри ChatCore. */}
      {showRightPanel && (
        <div
          className="lg:hidden fixed inset-x-0 top-[calc(var(--header-height,4.5rem)+3rem)] bottom-0 flex flex-col bg-white border-t border-neutral-border max-w-full overflow-x-hidden"
          style={{ zIndex: LAYERS.mobileOverlay }}
        >
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <AITutorSidebar
              user={user}
              courseTitle={course?.title || ""}
              lessonTitle={lessonMeta?.title || ""}
              lessonId={lessonId || ""}
              courseSlug={slug || ""}
              lessonContent={lesson?.content || ""}
              onClose={() => setShowRightPanel(false)}
              width={340}
              rightOffset={0}
              isEmbedded={true}
              onMinWidthChange={setChatPanelMinWidth}
              onboardingPageKey="lessonpage"
              onboardingCompletedPages={onboardingCompletedPages}
              onboardingStatusLoading={onboardingStatusLoading}
              onStartOnboarding={() => onboardingStartRef.current?.()}
              showChatHistoryInEmbedded={false}
              externalPrompt={externalTutorPrompt}
              tutorAsideMode={tutorAsideMode}
              onTutorAsideModeChange={setTutorAsideMode}
              roadmapIsMobile={true}
            />
          </div>
        </div>
      )}

      {/* Mobile: sidebar drawer */}
      {sidebarVisible && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50"
          style={{ zIndex: LAYERS.mobileBackdrop }}
          onClick={() => setSidebarVisible(false)}
          aria-hidden="true"
        />
      )}
      {sidebarVisible && (
        <aside
          className="lg:hidden fixed left-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-white border-r border-neutral-border shadow-xl overflow-y-auto"
          style={{ zIndex: LAYERS.mobileDrawer }}
        >
          <div className="p-4 border-b flex items-center justify-between">
            <span className="font-semibold">{course?.title || ""}</span>
            <button onClick={() => setSidebarVisible(false)} className="p-2 hover:bg-neutral-light rounded-lg" aria-label="Закрыть">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-4">
            {course?.modules.map((module) => (
              <div key={module.id} className="mb-4">
                <div className="text-sm font-semibold text-neutral-text mb-2">{module.title}</div>
                <div className="space-y-1">
                  {module.lessons.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => {
                        navigate(`/courses/${slug}/${l.id}`);
                        setSidebarVisible(false);
                        if ((progress[l.id] || "not_started") === "not_started") setLessonStatus(l.id, "in_progress");
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                        lesson?.id === l.id
                          ? "bg-primary/10 text-primary font-medium"
                          : "hover:bg-neutral-light"
                      }`}
                    >
                      {l.title}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
      )}

      {/* Модальное окно с планом модуля */}
      {selectedModuleId && course && (() => {
        const module = course.modules.find(m => m.id === selectedModuleId);
        if (!module) return null;
        
        const moduleLessons = module.lessons;
        const completedLessons = moduleLessons.filter(l => progress[l.id] === "done").length;
        const moduleProgress = moduleLessons.length > 0
          ? Math.round((completedLessons / moduleLessons.length) * 100)
          : 0;
        const totalTime = moduleLessons.reduce((sum, l) => sum + (l.estimatedTime || 0), 0);
        
        return (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
            onClick={() => setSelectedModuleId(null)}
          >
            <div 
              className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Заголовок модального окна */}
              <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-4 sm:p-6 border-b border-neutral-border">
                <div className="flex items-start justify-between gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                      </div>
                      <h2 className="text-lg sm:text-2xl font-heading font-bold text-neutral-text truncate">
                        {module.title}
                      </h2>
                    </div>
                    {module.description && (
                      <p className="text-xs sm:text-sm text-neutral-text/70 mb-2 sm:mb-3 line-clamp-2">
                        {module.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-neutral-text/60">
                      <span className="flex items-center gap-1.5">
                        <BookOpen className="w-4 h-4" />
                        {moduleLessons.length} уроков
                      </span>
                      {totalTime > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4" />
                          ~{totalTime} мин
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4" />
                        {moduleProgress}% завершено
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedModuleId(null)}
                    className="p-2 hover:bg-neutral-100 rounded-lg transition"
                    aria-label="Закрыть"
                  >
                    <X className="w-5 h-5 text-neutral-text/60" />
                  </button>
                </div>
                
                {/* Прогресс-бар */}
                <div className="mt-4 relative">
                  <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 relative ${
                        moduleProgress === 100 ? 'bg-primary' : moduleProgress > 0 ? 'bg-orange-400' : 'bg-neutral-300'
                      }`}
                      style={{ width: `${moduleProgress}%` }}
                    >
                      {moduleProgress === 100 && (
                        <span className="absolute inset-0 flex items-center justify-end pr-1 text-[10px] font-semibold text-white pointer-events-none">
                          100%
                        </span>
                      )}
                    </div>
                  </div>
                  {moduleProgress !== 100 && (
                    <span className="absolute -top-0.5 right-0 text-[10px] font-semibold text-neutral-text/70">
                      {moduleProgress}%
                    </span>
                  )}
                </div>
              </div>

              {/* Список уроков */}
              <div className="overflow-y-auto max-h-[calc(95vh-220px)] sm:max-h-[calc(90vh-240px)] p-3 sm:p-6">
                <div className="space-y-2">
                  {moduleLessons.map((l, idx) => {
                    const status = progress[l.id] || "not_started";
                    const isCompleted = status === "done";
                    const isInProgress = status === "in_progress";
                    const isActive = l.id === lessonId;
                    
                    return (
                      <button
                        key={l.id}
                        onClick={() => {
                          navigate(`/courses/${slug}/${l.id}`);
                          setSelectedModuleId(null);
                          // Прокручиваем к началу страницы на мобильных устройствах
                          if (isMobile) {
                            setTimeout(() => {
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }, 100);
                          }
                        }}
                        className={`w-full flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg sm:rounded-xl border transition-all group ${
                          isActive
                            ? "border-primary bg-primary/5"
                            : "border-neutral-border hover:border-primary/50 hover:bg-primary/5"
                        }`}
                      >
                        {/* Номер и статус */}
                        <div className="flex flex-col items-center gap-2 pt-0.5 sm:pt-1 flex-shrink-0">
                          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center font-semibold text-xs sm:text-sm transition-all ${
                            isCompleted
                              ? 'bg-primary text-white'
                              : isInProgress
                              ? 'bg-orange-400 text-white'
                              : isActive
                              ? 'bg-primary/20 text-primary'
                              : 'bg-neutral-100 text-neutral-text/50 group-hover:bg-primary/10 group-hover:text-primary'
                          }`}>
                            {isCompleted ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <span>{idx + 1}</span>
                            )}
                          </div>
                        </div>

                        {/* Информация об уроке */}
                        <div className="flex-1 text-left min-w-0">
                          <h3 className={`text-sm sm:text-base font-semibold mb-1 transition-colors ${
                            isActive
                              ? "text-primary"
                              : "text-neutral-text group-hover:text-primary"
                          }`}>
                            {l.title}
                          </h3>
                          {l.summary && (
                            <p className="text-xs sm:text-sm text-neutral-text/60 mb-1.5 sm:mb-2 line-clamp-2">
                              {l.summary}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-neutral-text/50">
                            {l.estimatedTime && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {l.estimatedTime} мин
                              </span>
                            )}
                            {isCompleted && (
                              <span className="flex items-center gap-1 text-primary">
                                <CheckCircle className="w-3 h-3" />
                                Завершено
                              </span>
                            )}
                            {isInProgress && (
                              <span className="flex items-center gap-1 text-orange-400">
                                <PlayCircle className="w-3 h-3" />
                                В процессе
                              </span>
                            )}
                            {isActive && (
                              <span className="flex items-center gap-1 text-primary">
                                Текущий урок
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};

// Функция для транслитерации кириллицы в латиницу
const transliterate = (text: string): string => {
  const map: Record<string, string> = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'Yo',
    'Ж': 'Zh', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
    'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
    'Ф': 'F', 'Х': 'H', 'Ц': 'Ts', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Sch',
    'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya'
  };
  
  return text.split('').map(char => map[char] || char).join('');
};

// Функция для генерации id из текста заголовка
const generateHeadingId = (text: string): string => {
  if (typeof text !== 'string' || !text.trim()) {
    return '';
  }
  return transliterate(text)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Удаляем спецсимволы (кроме букв, цифр, пробелов и дефисов)
    .replace(/\s+/g, '-') // Заменяем пробелы на дефисы
    .replace(/-+/g, '-') // Убираем множественные дефисы
    .replace(/^-|-$/g, ''); // Убираем дефисы в начале и конце
};

// Компонент для рендеринга контента урока с поддержкой блоков кода
const LessonContent = ({ 
  content, 
  user, 
  quiz,
  onTaskCompleted,
  courseSlug,
  lessonId,
  onAskTutor,
}: { 
  content: string; 
  user: TelegramUser | null; 
  quiz?: Lesson["quiz"];
  onTaskCompleted?: (taskId: number) => void;
  courseSlug?: string;
  lessonId?: string;
  onAskTutor?: (prompt: string) => void;
}) => {
  // Парсим контент и находим блоки кода в формате:
  // [code:task_id:initial_sql] - упражнение с проверкой
  // [demo:sql_code] - демонстрация без проверки
  // [check:sql_code:checks_json] - проверка SQL с произвольными checks
  // [quiz:quiz_json] - квиз прямо в контенте
  // [tutor:buttons_json] - кнопки для запуска AI Tutor из урока
  const parts = useMemo(() => {
    const result: Array<{ 
      type: "text" | "code" | "demo" | "check" | "quiz" | "tutor"; 
      content?: string; 
      taskId?: number; 
      initialSql?: string; 
      isDemo?: boolean;
      checks?: Array<Record<string, any>>;
      quizQuestions?: Array<{ id: string; question: string; options: string[]; correctAnswer?: number; correctAnswers?: number[]; multipleChoice?: boolean; explanation?: string; questionNumber?: string }>;
      tutorPrompts?: Array<{ label: string; prompt: string }>;
    }> = [];
    let currentIndex = 0;

    while (currentIndex < content.length) {
      // Ищем начало любого блока
      const codeStart = content.indexOf("[code:", currentIndex);
      const demoStart = content.indexOf("[demo:", currentIndex);
      const checkStart = content.indexOf("[check:", currentIndex);
      const quizStart = content.indexOf("[quiz:", currentIndex);
      const tutorStart = content.indexOf("[tutor:", currentIndex);
      
      // Находим ближайший блок
      const starts = [
        { index: codeStart, type: "code" as const },
        { index: demoStart, type: "demo" as const },
        { index: checkStart, type: "check" as const },
        { index: quizStart, type: "quiz" as const },
        { index: tutorStart, type: "tutor" as const },
      ].filter(s => s.index !== -1);
      
      if (starts.length === 0) {
        // Больше блоков нет, добавляем оставшийся текст
        if (currentIndex < content.length) {
          result.push({
            type: "text",
            content: content.substring(currentIndex),
          });
        }
        break;
      }

      // Сортируем по позиции и берем первый
      starts.sort((a, b) => a.index - b.index);
      const { index: blockStart, type: blockType } = starts[0];

      // Добавляем текст до блока
      if (blockStart > currentIndex) {
        result.push({
          type: "text",
          content: content.substring(currentIndex, blockStart),
        });
      }

      // Для блоков с JSON (quiz, check) используем умный поиск с учетом баланса скобок
      // Для остальных - простой поиск первой закрывающей скобки
      let blockEnd = -1;
      if (blockType === "quiz" || blockType === "check" || blockType === "tutor") {
        // Новый (более надежный) поиск конца блока: считаем глубину квадратных скобок всего markdown-блока,
        // начиная с '[' в blockStart. Это корректно обрабатывает случаи вида:
        // [check:SQL:[{...},{...}]]  -> блок закрывается на второй ']'
        // [quiz:{...}]              -> блок закрывается на единственной ']'
        // [quiz:[{...},{...}]]      -> блок закрывается на второй ']'
        //
        // Важно: игнорируем скобки внутри строк (") и экранированные символы.
        let blockSquareDepth = 1;
        let blockInString = false;
        let blockEscapeNext = false;
        for (let i = blockStart + 1; i < content.length; i++) {
          const ch = content[i];
          if (blockEscapeNext) {
            blockEscapeNext = false;
            continue;
          }
          if (ch === "\\") {
            blockEscapeNext = true;
            continue;
          }
          if (ch === "\"") {
            blockInString = !blockInString;
            continue;
          }
          if (blockInString) continue;
          if (ch === "[") {
            blockSquareDepth++;
          } else if (ch === "]") {
            blockSquareDepth--;
            if (blockSquareDepth === 0) {
              blockEnd = i;
              break;
            }
          }
        }

        // Fallback: если новый алгоритм не нашел конец блока, используем старую логику (с логами) ниже.
        if (blockEnd !== -1) {
          // blockEnd нашли — пропускаем старую логику поиска
        } else {
        // Для JSON блоков ищем закрывающую скобку с учетом баланса скобок (квадратных и фигурных) и кавычек
        const prefixLength = blockType === "check" ? 7 : 6;
        const searchStart = blockStart + prefixLength;
        let squareBracketDepth = 0; // Глубина квадратных скобок []
        let curlyBracketDepth = 0; // Глубина фигурных скобок {}
        let inString = false;
        let escapeNext = false;
        let jsonStartChar = null; // Первый символ JSON ([ или {)
        let jsonStartIndex = -1; // Позиция начала JSON
        let jsonArrayClosed = false; // Флаг для check блока: JSON массив закрыт, ищем следующую ]
        
        for (let i = searchStart; i < content.length; i++) {
          const char = content[i];
          
          if (escapeNext) {
            escapeNext = false;
            continue;
          }
          
          if (char === '\\') {
            escapeNext = true;
            continue;
          }
          
          if (char === '"' && !escapeNext) {
            inString = !inString;
            continue;
          }
          
          if (!inString) {
            // Отслеживаем начало JSON (первая [ или {)
            // Для check блока JSON начинается после SQL и двоеточия, поэтому ищем [ или { после возможного двоеточия
            if (jsonStartChar === null && (char === '[' || char === '{')) {
              // Для check блока нужно убедиться, что это начало JSON, а не часть SQL
              // Если перед этим есть двоеточие или это начало блока, это начало JSON
              if (blockType === "check") {
                // Для check блока JSON начинается после SQL и двоеточия
                // Проверяем, что перед этим есть двоеточие (разделитель SQL и JSON)
                const beforeChar = content.substring(Math.max(searchStart, i - 20), i).trim();
                if (!beforeChar.endsWith(':')) {
                  // Это не начало JSON, пропускаем
                  continue;
                }
              }
              jsonStartChar = char;
              jsonStartIndex = i;
              // Устанавливаем начальную глубину
              if (char === '[') {
                squareBracketDepth = 1;
              } else {
                curlyBracketDepth = 1;
              }
              continue;
            }
            
            if (jsonStartChar !== null) {
              if (char === '[') {
                squareBracketDepth++;
              } else if (char === ']') {
                squareBracketDepth--;
              } else if (char === '{') {
                curlyBracketDepth++;
              } else if (char === '}') {
                curlyBracketDepth--;
              }
              
              // Закрывающая скобка блока - это ], когда все скобки закрыты
              // Для JSON, начинающегося с [, нужно squareBracketDepth === 0 И curlyBracketDepth === 0
              // Для JSON, начинающегося с {, нужно curlyBracketDepth === 0 И squareBracketDepth === 0
              // Важно: для check блока JSON массив закрывается на ], но markdown блок закрывается на следующей ]
              // Поэтому для check блока нужно найти следующую ] после закрытия JSON массива
              if (char === ']') {
                // Проверяем, закрыт ли JSON массив (squareBracketDepth === 0 после уменьшения)
                const jsonClosed = squareBracketDepth === 0 && curlyBracketDepth === 0;
                
                if (blockType === "check" && jsonArrayClosed && jsonClosed) {
                  // Для check блока это вторая ], которая закрывает markdown блок
                  // (JSON массив уже был закрыт на предыдущей ], теперь закрывается markdown блок)
                  blockEnd = i;
                  break;
                } else if (jsonStartChar === '[' && jsonClosed) {
                  // JSON начинался с [, все скобки закрыты
                  // Для check блока это закрывающая скобка JSON массива, markdown блок закрывается следующей ]
                  if (blockType === "check") {
                    // Для check блока нужно найти следующую ] после закрытия JSON массива
                    // Устанавливаем флаг, что JSON массив закрыт, и продолжаем поиск
                    jsonArrayClosed = true;
                    // Продолжаем поиск следующей ]
                    continue;
                  } else {
                    // Для quiz блока это закрывающая скобка markdown блока
                    blockEnd = i;
                    break;
                  }
                } else if (jsonStartChar === '{' && jsonClosed) {
                  // JSON начинался с {, все скобки закрыты, это закрывающая скобка блока
                  blockEnd = i;
                  break;
                }
              }
            }
          }
        }
        } // end fallback old logic
      } else {
        // Для обычных блоков ищем первую закрывающую скобку
        blockEnd = content.indexOf("]", blockStart);
      }
      
      if (blockEnd === -1) {
        // Не нашли закрывающую скобку, добавляем как текст
        result.push({
          type: "text",
          content: content.substring(blockStart),
        });
        break;
      }

      const prefixLength =
        blockType === "code"
          ? 6
          : blockType === "demo"
            ? 6
            : blockType === "check"
              ? 7
              : blockType === "tutor"
                ? 7
                : 6;
      const blockContent = content.substring(blockStart + prefixLength, blockEnd);

      if (blockType === "code") {
        // Извлекаем содержимое блока [code:task_id:initial_sql]
        const colonIndex = blockContent.indexOf(":");
        
        if (colonIndex === -1) {
          // Нет двоеточия, только task_id
          const taskId = parseInt(blockContent.trim(), 10);
          if (!isNaN(taskId)) {
            result.push({
              type: "code",
              taskId,
              initialSql: "",
              isDemo: false,
            });
          }
        } else {
          // Есть двоеточие, извлекаем task_id и initial_sql
          const taskId = parseInt(blockContent.substring(0, colonIndex).trim(), 10);
          const initialSql = blockContent.substring(colonIndex + 1).trim();
          
          if (!isNaN(taskId)) {
            result.push({
              type: "code",
              taskId,
              initialSql,
              isDemo: false,
            });
          }
        }
      } else if (blockType === "demo") {
        // Извлекаем содержимое блока [demo:sql_code]
        const sqlCode = blockContent.trim();
        result.push({
          type: "demo",
          initialSql: sqlCode,
          isDemo: true,
        });
      } else if (blockType === "check") {
        // Извлекаем содержимое блока [check:sql_code:checks_json]
        // Для check блока нужно найти разделитель между SQL и JSON
        // JSON начинается с [ или {, ищем первое вхождение после SQL запроса
        
        // Ищем начало JSON (первая [ или { после возможных двоеточий в SQL)
        let jsonStartIndex = -1;
        for (let i = 0; i < blockContent.length; i++) {
          const char = blockContent[i];
          if (char === '[' || char === '{') {
            // Проверяем, что это не часть SQL (например, не внутри строки или комментария)
            // Простая эвристика: если перед этим есть двоеточие и пробелы/переносы, это начало JSON
            const beforeChar = blockContent.substring(Math.max(0, i - 10), i).trim();
            if (beforeChar.endsWith(':') || beforeChar.endsWith('],') || beforeChar.endsWith('},')) {
              jsonStartIndex = i;
              break;
            }
          }
        }
        
        if (jsonStartIndex === -1) {
          // Не нашли начало JSON, возможно только SQL код
          result.push({
            type: "check",
            initialSql: blockContent.trim(),
            checks: [],
          });
        } else {
          const sqlCode = blockContent.substring(0, jsonStartIndex).trim();
          // Убираем возможное двоеточие в конце SQL
          const sqlCodeClean = sqlCode.endsWith(':') ? sqlCode.slice(0, -1).trim() : sqlCode;
          const checksJson = blockContent.substring(jsonStartIndex).trim();
          try {
            const checks = JSON.parse(checksJson);
            result.push({
              type: "check",
              initialSql: sqlCodeClean,
              checks: Array.isArray(checks) ? checks : [checks],
            });
          } catch (e) {
            console.error("Ошибка парсинга checks JSON:", e);
            result.push({
              type: "check",
              initialSql: sqlCodeClean,
              checks: [],
            });
          }
        }
      } else if (blockType === "quiz") {
        // Извлекаем содержимое блока [quiz:question_json]
        // Квиз всегда состоит из одного вопроса
        try {
          const questionData = JSON.parse(blockContent.trim());
          // Если это массив, берем первый элемент, иначе сам объект
          const question = Array.isArray(questionData) ? questionData[0] : questionData;
          // Проверяем, что есть либо correctAnswer (одиночный выбор), либо correctAnswers (множественный выбор)
          const hasValidAnswer = 
            question && 
            question.id && 
            question.question && 
            question.options &&
            (question.correctAnswer !== undefined || (question.correctAnswers && Array.isArray(question.correctAnswers)));
          if (hasValidAnswer) {
            result.push({
              type: "quiz",
              quizQuestions: [question],
            });
          } else {
            console.error("Некорректный формат вопроса квиза:", question);
          }
        } catch (e) {
          console.error("Ошибка парсинга quiz JSON:", e);
        }
      } else if (blockType === "tutor") {
        try {
          const buttonsData = JSON.parse(blockContent.trim());
          const buttons = Array.isArray(buttonsData) ? buttonsData : [buttonsData];
          const validButtons = buttons
            .filter((item) => item && typeof item.label === "string" && typeof item.prompt === "string")
            .map((item) => ({
              label: item.label.trim(),
              prompt: item.prompt.trim(),
            }))
            .filter((item) => item.label && item.prompt);

          if (validButtons.length > 0) {
            result.push({
              type: "tutor",
              tutorPrompts: validButtons,
            });
          }
        } catch (e) {
          console.error("Ошибка парсинга tutor JSON:", e);
        }
      }

      currentIndex = blockEnd + 1;
    }

    // Если не было блоков, возвращаем весь контент как текст
    if (result.length === 0) {
      return [{ type: "text" as const, content }];
    }
    return result;
  }, [content]);

  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Функция для обработки клика по заголовку (копирование ссылки)
  const handleHeadingClick = (id: string, event: React.MouseEvent) => {
    // Не предотвращаем переход, но копируем ссылку
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
    // Переход произойдет автоматически через href
  };

  // Функция для извлечения текста из ReactNode
  const getTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') return children;
    if (typeof children === 'number') return String(children);
    if (Array.isArray(children)) {
      return children.map(getTextFromChildren).join('');
    }
    if (children && typeof children === 'object' && 'props' in children) {
      return getTextFromChildren((children as any).props?.children || '');
    }
    return '';
  };

  return (
    <div className="prose prose-sm max-w-none text-neutral-text/90">
      {parts.map((part, index) => {
        if (part.type === "text") {
          return (
            <div key={index} className="mb-6 last:mb-0">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => {
                    const text = getTextFromChildren(children);
                    const id = generateHeadingId(text);
                    const isCopied = copiedId === id;
                    return (
                      <h1 
                        id={id}
                        className="text-2xl font-bold mt-6 mb-2 text-neutral-text group relative scroll-mt-4 pl-8"
                      >
                        <a
                          href={`#${id}`}
                          onClick={(e) => handleHeadingClick(id, e)}
                          className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all text-primary hover:text-primary-dark no-underline flex items-center justify-center ${
                            isCopied ? 'opacity-100 text-green-600' : 'opacity-0 group-hover:opacity-100'
                          }`}
                          aria-label={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку на заголовок"}
                          title={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку"}
                        >
                          <LinkIcon className="w-5 h-5" />
                        </a>
                        {children}
                      </h1>
                    );
                  },
                  h2: ({ children }) => {
                    const text = getTextFromChildren(children);
                    const id = generateHeadingId(text);
                    const isCopied = copiedId === id;
                    return (
                      <h2 
                        id={id}
                        className="text-xl font-semibold mt-6 mb-2 text-neutral-text group relative scroll-mt-4 pl-7"
                      >
                        <a
                          href={`#${id}`}
                          onClick={(e) => handleHeadingClick(id, e)}
                          className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all text-primary hover:text-primary-dark no-underline flex items-center justify-center ${
                            isCopied ? 'opacity-100 text-green-600' : 'opacity-0 group-hover:opacity-100'
                          }`}
                          aria-label={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку на заголовок"}
                          title={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку"}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </a>
                        {children}
                      </h2>
                    );
                  },
                  h3: ({ children }) => {
                    const text = getTextFromChildren(children);
                    const id = generateHeadingId(text);
                    const isCopied = copiedId === id;
                    return (
                      <h3 
                        id={id}
                        className="text-lg font-semibold mt-5 mb-2 text-neutral-text group relative scroll-mt-4 pl-7"
                      >
                        <a
                          href={`#${id}`}
                          onClick={(e) => handleHeadingClick(id, e)}
                          className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all text-primary hover:text-primary-dark no-underline flex items-center justify-center ${
                            isCopied ? 'opacity-100 text-green-600' : 'opacity-0 group-hover:opacity-100'
                          }`}
                          aria-label={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку на заголовок"}
                          title={isCopied ? "Ссылка скопирована!" : "Скопировать ссылку"}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </a>
                        {children}
                      </h3>
                    );
                  },
                  p: ({ children }) => <p className="mb-3 text-neutral-text/90 leading-relaxed">{children}</p>,
                  img: ({ src, alt }) => {
                    // Преобразуем относительные пути к изображениям в URL API
                    let imageSrc = src || '';
                    if (imageSrc && !imageSrc.startsWith('http') && !imageSrc.startsWith('//') && courseSlug) {
                      // Убираем начальные ./ или / из пути
                      const cleanPath = imageSrc.replace(/^\.?\//, '');
                      // Формируем URL через API
                      imageSrc = `${API_HOST}/api/content/courses/${courseSlug}/assets/${cleanPath}`;
                    }
                    return (
                      <img 
                        src={imageSrc} 
                        alt={alt || ''} 
                        className="my-4 rounded-lg shadow-sm max-w-full h-auto"
                        loading="lazy"
                        decoding="async"
                        style={{ aspectRatio: 'auto' }}
                      />
                    );
                  },
                  a: ({ href, children }) => (
                    <a 
                      href={href} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
                    >
                      {children}
                    </a>
                  ),
                  ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1 text-neutral-text/90">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1 text-neutral-text/90">{children}</ol>,
                  li: ({ children }) => <li className="ml-4">{children}</li>,
                  code: ({ inline, children }) => 
                    inline ? (
                      <code className="bg-neutral-light rounded px-1.5 py-0.5 text-sm text-neutral-text font-mono">{children}</code>
                    ) : (
                      <code className="block bg-neutral-light rounded p-3 text-sm text-neutral-text font-mono whitespace-pre overflow-x-auto">{children}</code>
                    ),
                  strong: ({ children }) => <strong className="font-semibold text-neutral-text">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                }}
              >
                {part.content || ""}
              </ReactMarkdown>
            </div>
          );
        } else if (part.type === "code") {
          return (
            <CourseCodeBlock
              key={index}
              taskId={part.taskId}
              initialSql={part.initialSql}
              user={user}
              isDemo={false}
              onTaskCompleted={onTaskCompleted}
            />
          );
        } else if (part.type === "demo") {
          return (
            <CourseCodeBlock
              key={index}
              initialSql={part.initialSql}
              user={user}
              isDemo={true}
            />
          );
        } else if (part.type === "check") {
          return (
            <InlineSQLCheck
              key={index}
              initialSql={part.initialSql || ""}
              checks={part.checks || []}
              user={user}
            />
          );
        } else if (part.type === "quiz") {
          return (
            <div key={index} data-onboarding-id="quiz">
              <Quiz questions={part.quizQuestions || []} />
            </div>
          );
        } else if (part.type === "tutor") {
          return (
            <div key={index} className="mb-6 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-neutral-text">Спросить AI Tutor</div>
                  <p className="mt-1 text-sm text-neutral-text/70">
                    Нажмите на готовый вопрос: AI Tutor откроется и сразу начнет отвечать в контексте текущего урока.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(part.tutorPrompts || []).map((item) => (
                      <button
                        key={`${item.label}-${item.prompt}`}
                        type="button"
                        onClick={() => onAskTutor?.(item.prompt)}
                        className="min-h-[44px] rounded-lg border border-primary/30 bg-white px-3 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-primary/10 hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        }
      })}
      {quiz && quiz.length > 0 && (
        <div data-onboarding-id="quiz">
          <Quiz questions={quiz} />
        </div>
      )}
    </div>
  );
};

// Компонент боковой панели AI Tutor
interface AITutorSidebarProps {
  user: TelegramUser | null;
  courseTitle: string;
  lessonTitle: string;
  lessonId: string;
  courseSlug: string;
  lessonContent: string;
  onClose: () => void;
  width?: number;
  rightOffset?: number;
  isEmbedded?: boolean;
  onMinWidthChange?: (w: number) => void;
  onboardingPageKey?: string;
  onboardingCompletedPages?: string[];
  onboardingStatusLoading?: boolean;
  onStartOnboarding?: () => void;
  showChatHistoryInEmbedded?: boolean;
  externalPrompt?: { id: string; text: string } | null;
  tutorAsideMode: AITutorDualMode;
  onTutorAsideModeChange: (mode: AITutorDualMode) => void;
  roadmapIsMobile: boolean;
}

const AITutorSidebar = ({
  user,
  lessonTitle,
  lessonId,
  courseSlug,
  lessonContent,
  onClose,
  width = 400,
  rightOffset = 0,
  isEmbedded = false,
  onMinWidthChange,
  onboardingPageKey,
  onboardingCompletedPages,
  onboardingStatusLoading,
  onStartOnboarding,
  showChatHistoryInEmbedded = true,
  externalPrompt,
  tutorAsideMode,
  onTutorAsideModeChange,
  roadmapIsMobile,
}: AITutorSidebarProps) => (
  <div
    className={`${isEmbedded ? "h-full min-w-0 flex-1" : "fixed top-0 h-full"} bg-white ${isEmbedded ? "" : "shadow-2xl z-50"} flex flex-col overflow-hidden ${isEmbedded ? "" : "border-l border-neutral-border"}`}
    style={isEmbedded ? {} : { width: `${width}px`, right: `${rightOffset}px` }}
  >
    <AITutorDualModePanel
      user={user}
      onClose={onClose}
      embedded={isEmbedded}
      onMinWidthChange={onMinWidthChange}
      mode={tutorAsideMode}
      onModeChange={onTutorAsideModeChange}
      chatProps={{
        contextType: "lesson",
        embedded: isEmbedded,
        lessonId: lessonId || "",
        lessonTitle,
        lessonContent,
        courseSlug: courseSlug || "",
        showCloseButton: false,
        containerClassName: "flex-1 min-h-0 flex flex-col overflow-hidden",
        showChatHistoryInEmbedded: isEmbedded && showChatHistoryInEmbedded,
        onMinWidthChange,
        onboardingPageKey,
        onboardingCompletedPages,
        onboardingStatusLoading,
        onStartOnboarding,
        externalPrompt,
        enableFeedback: true,
      }}
      roadmapProps={{ isMobile: roadmapIsMobile }}
    />
  </div>
);
